import type { Auth, User } from "firebase/auth";
import { signOut } from "./signOut";
import { useEffect, useMemo, useRef, useState } from "react";
import * as api from "./api";
import { SourceTag } from "./SourceTag";
import { dayTitle, STATE_TEXT as ROUTE_STATE_TEXT } from "./driverRoutes";
import { go, useTitle } from "./hooks";
import { PhotoAdd } from "./PhotoPages";
import { RouteCheckSummary, RouteFinishForm, RouteStartForm } from "./RouteCheck";
import { GallonsFields, NoScrapConfirm } from "./GallonsFields";
import { PlaceConfirm } from "./PlaceConfirm";
import { bucket, decidePlace, FAR_M, isPhone, needsCheck, readFix, stopPin, takeHint } from "./place";
import { asksNoScrap, capacityKey, capacityWarning, type Gallons, gallonsText, parseGallons } from "./pickup";
import { accuracyText, navigationUrl, navTarget, restoredText, tankUndo } from "./location";
import { OffRouteMark } from "./OffRouteMark";
import { RequestedMark } from "./RequestedMark";
import { PinHistory } from "./PinHistory";
import { saveFailure, SERVER_PROBLEM } from "./save";
import { clockTime } from "./format";
import { localNow } from "./visits";
import { checkingText, dayKeys, dayRefreshDue, isDayReady, isNetworkError, offlineText, unlessSaved } from "./offline/cache";
import { userScope } from "./offline/db";
import { refreshDay } from "./offline/day";
import { changeEntry, entryStates, entryStop, loadEntry, stopPhotoCount, pickupEntry, startEntry, finishEntry, stopEntry, withLocal, withLocalLoads } from "./offline/local";
import { enqueue, entries as outboxEntries, lastSent, NO_STORAGE, type NewEntry, type OutboxEntry, SECOND_PICKUP, stateText, subscribe } from "./offline/outbox";
import { loadToday, type TodayData } from "./offline/pages";
import { InstallNote } from "./offline/Install";
import { useOutbox } from "./offline/useOutbox";
import { CHANGE_LABEL, canChange, changeHint, changeRefusalText, entryRefusalText, FIELD_TEXT, withButton } from "./offline/messages";
import {
  changeReopen,
  DRAFT_ASK,
  DRAFT_KEEP,
  doneElsewhere,
  doneElsewhereText,
  heldGallons,
  otherPickupKept,
  otherPickupText,
  pickupHeld,
  pickupSavedShown,
  pickupSavedText,
  savedGallons,
  secondPickupText,
  serverGallons,
  serverPickupText,
  stopButton,
  stopDraft,
  stopPickup,
  stopReopen,
  todayReason,
} from "./visitFix";
import { oneAtATime, watchDay } from "./offline/reload";
import { isReloading, useHoldReload } from "./offline/hold";
import {
  afterSave,
  ASK_OFFICE,
  canChangeStop,
  changeButton,
  changeFields,
  changeResultShown,
  CHANGING_TEXT,
  checkWaitingText,
  currentLoad,
  ensureLoad as openLoad,
  entriesToKeep,
  entryOutcome,
  isChange,
  keptEntries,
  keptStatusText,
  netText,
  nextStop,
  notSavedText,
  plannedEntries,
  OUTCOME_ORDER,
  OUTCOMES,
  type Outcome,
  type OutcomeField,
  outcomeInput,
  outcomeSaved,
  outcomeText,
  plannedText,
  progressText,
  reloadsDay,
  routeDayOver,
  savedValues,
  telHref,
  windowText,
  withTankPin,
} from "./today";
import { Skeleton } from "./Skeleton";

type DayLoad = api.RouteDay | "hidden" | "session" | "server" | "network" | undefined;

interface Entry {
  text: string;
  scrap: string;
  pickupId: string;
  pickupSaved: boolean;
  error: string | null;
  errorAt?: OutcomeField | null;
  outcome: Outcome | null;
  choice: string;
  other: string;
  left: string;
  askNoScrap?: boolean;
  /** The gallons that the driver saves again over the container size, as on the account page. */
  overCapacity?: string;
  /** Rule D-19: the phone is away from the stop, and the driver must confirm before the save. */
  askPlace?: { place: api.Place; outcome: Outcome | null; noScrap: boolean; hint: boolean };
  /** The refused stop entry that is in the form, or that waits for the driver to answer the question. */
  fixing?: string;
  askReplace?: boolean;
  /** The client id of the change of a saved stop result that is in the form (SO-1033). */
  changing?: string;
}

const blank = (): Entry => ({ text: "", scrap: "", pickupId: crypto.randomUUID(), pickupSaved: false, error: null, outcome: null, choice: "", other: "", left: "" });
const OUTCOME_TEST_ID: Record<Outcome, string> = { skipped: "today-skip", measured: "today-measured", partial: "today-partial", no_access: "today-no-access" };
const DRAFTS = "today-drafts";
const CHANGE_DONE = changeButton(null);
const NONE: OutboxEntry[] = [];

const readKept = (raw: string | null): Record<string, Entry> =>
  Object.fromEntries(Object.entries(keptEntries(raw)).map(([id, kept]) => [id, { ...blank(), ...kept }]));

type Status = { ok: boolean; text: string; session?: boolean; entry?: string };

const scrollToStop = (stopId: string) => document.getElementById(`stop-${stopId}`)?.scrollIntoView({ block: "start", behavior: "smooth" });

type PinNote = { ok: boolean; text: string };

export function TodayPage({
  slug,
  base,
  org,
  auth,
  user,
  canView,
  canVisit = false,
}: {
  slug: string;
  base: string;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  canView: boolean | undefined;
  canVisit?: boolean;
}) {
  const [found, setFound] = useState<DayLoad>(undefined);
  const [loads, setLoads] = useState<api.Load[]>([]);
  const [canLoad, setCanLoad] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const retried = useRef(false);
  const tryAgain = () => {
    retried.current = true;
    setAttempt((n) => n + 1);
  };
  const [entries, setEntries] = useState<Record<string, Entry>>({});
  const draftsRead = useRef(false);
  const scrollTo = useRef<{ scrollId: string | null; focusId: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [dayAt, setDayAt] = useState(0);
  const [status, setStatus] = useState<Status | null>(null);
  const [pinNotes, setPinNotes] = useState<Record<string, PinNote>>({});
  const [pinUndos, setPinUndos] = useState<Record<string, api.LocationChange>>({});
  const [changed, setChanged] = useState<Record<string, { text: string; entry: string }>>({});
  const live = useRef(true);
  const saves = useRef(0);
  const editFound: typeof setFound = (next) => {
    saves.current += 1;
    setFound(next);
  };
  const scope = useMemo(() => userScope(slug, user.uid), [slug, user.uid]);
  const queued = useOutbox(scope).all ?? NONE;
  const states = useMemo(() => entryStates(queued), [queued]);
  const [offlineAt, setOfflineAt] = useState<number | null>(null);
  const [readyAt, setReadyAt] = useState<number | null>(null);
  const [checking, setChecking] = useState(false);
  const [clockDay, setClockDay] = useState(() => localNow(new Date(), org.time_zone).day);
  useEffect(() => {
    const tick = () => setClockDay(localNow(new Date(), org.time_zone).day);
    tick();
    const timer = setInterval(tick, 60_000);
    return () => clearInterval(timer);
  }, [org.time_zone]);
  useTitle(`Route · ${org.name}`);
  useEffect(() => {
    if (canView !== true) return;
    live.current = true;
    setFound(undefined);
    let active = true;
    const asked = Date.now();
    const failed = (e: unknown) => {
      const code = e instanceof api.ApiError ? e.status : 0;
      if (active) setFound(code === 404 ? "hidden" : code === 401 ? "session" : code >= 500 ? "server" : "network");
    };
    const show = async ({ day, loads: listed, at, fresh }: TodayData) => {
      if (!active) return;
      setLoads(listed?.loads ?? []);
      setCanLoad(Boolean(listed?.can_make));
      setFound(day);
      setDayAt(at ?? asked);
      setOfflineAt(at);
      setChecking(Boolean(fresh));
      if (fresh)
        unlessSaved(fresh, saves).then(
          (data) => {
            if (data) return show(data);
            if (!active) return;
            setChecking(false);
            setOfflineAt(null);
          },
          (e) => {
            if (!active) return;
            setChecking(false);
            if (!isNetworkError(e)) failed(e);
          },
        );
      if (at !== null) return;
      const kept = await isDayReady(scope, day.today);
      if (active && kept) setReadyAt(kept.at);
      const retry = retried.current;
      retried.current = false;
      const changedAt = lastSent((await outboxEntries(scope)).filter((entry) => entry.kind === "start" || entry.kind === "finish"));
      if (!dayRefreshDue(kept?.at ?? null, retry, Date.now(), changedAt)) return;
      const why = changedAt !== null && kept && changedAt > kept.at ? "route start or finish" : retry ? "retry" : kept ? "old" : "none kept";
      const refreshed = await user.getIdToken().then((token) => refreshDay(scope, token, day)).catch(() => null);
      console.info(`today: day pack refresh (${why}): ${refreshed ? "kept" : "failed"}`);
      if (active && refreshed) setReadyAt(refreshed);
    };
    loadToday(scope, () => user.getIdToken(), localNow(new Date(), org.time_zone).day)
      .then(show)
      .catch(failed);
    return () => {
      active = false;
      live.current = false;
    };
  }, [user, attempt, canView, scope, org.time_zone]);
  useEffect(() => {
    if (typeof found === "object" && offlineAt === null) void scope.write(dayKeys.route(found.today), found);
  }, [found, offlineAt, scope]);
  useEffect(() => {
    if (typeof found === "object" && offlineAt === null) void scope.write(dayKeys.loads(found.today), { loads, can_make: canLoad });
  }, [loads, canLoad, found, offlineAt, scope]);
  useEffect(() => {
    draftsRead.current = false;
    void scope.read<string>(DRAFTS).then((kept) => {
      draftsRead.current = true;
      setEntries((all) => ({ ...readKept(kept?.value ?? null), ...all }));
    });
  }, [scope]);
  useEffect(() => {
    if (draftsRead.current) void scope.write(DRAFTS, JSON.stringify(entriesToKeep(entries)));
  }, [entries, scope]);
  const quietLoad = async () => {
    if (found === undefined || found === "hidden") return;
    const asked = Date.now();
    const token = await user.getIdToken();
    const [day, listed] = await Promise.all([api.routeDay(token), api.loads(token, localNow(new Date(), org.time_zone).day).catch(() => null)]);
    if (!live.current) return;
    setFound(day);
    if (listed) {
      setLoads(listed.loads);
      setCanLoad(Boolean(listed.can_make));
    }
    setDayAt(asked);
    setOfflineAt(null);
    setChecking(false);
  };
  const quietRef = useRef(quietLoad);
  quietRef.current = quietLoad;
  const reloadDayQuietly = useMemo(() => oneAtATime(() => quietRef.current()), []);
  useEffect(() => (canView === true ? watchDay(scope, () => void reloadDayQuietly()) : undefined), [scope, canView, reloadDayQuietly]);
  useEffect(
    () =>
      subscribe(scope, (change) => {
        if (!live.current || !change) return;
        const { entry, result } = change;
        if (entry.kind === "load" && entry.state === "sent") {
          const load = (result as { load?: api.Load | null } | null)?.load;
          if (load) setLoads((all) => [...all.filter((l) => l.id !== load.id), load]);
        }
        if (!["start", "stop", "finish"].includes(entry.kind) || entry.state !== "sent") return;
        const next = result as api.RouteDay | null;
        setFound((day) => (typeof day === "object" && next?.routes && next.day === day.day ? next : day));
        if (next?.routes) setDayAt(entry.sent_at ?? Date.now());
      }),
    [scope],
  );
  const shownDay = useMemo(() => (typeof found === "object" ? withLocal(found, queued, dayAt) : found), [found, queued, dayAt]);
  const shownLoads = useMemo(() => withLocalLoads(loads, queued), [loads, queued]);
  useEffect(() => {
    if (typeof shownDay !== "object") return;
    setEntries((all) => {
      const open = { ...plannedEntries(all, shownDay.routes), ...Object.fromEntries(Object.entries(all).filter(([, entry]) => entry.changing)) };
      return Object.keys(open).length === Object.keys(all).length ? all : open;
    });
  }, [shownDay]);
  useEffect(() => {
    const target = scrollTo.current;
    if (!target || typeof shownDay !== "object") return;
    scrollTo.current = null;
    // Scroll two frames later, when the saved card has its collapsed height.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if (target.scrollId) document.getElementById(target.scrollId)?.scrollIntoView({ block: "start", behavior: "auto" });
        const focus = document.getElementById(target.focusId);
        if (focus && focus.getBoundingClientRect().bottom > innerHeight) focus.scrollIntoView({ block: "start", behavior: "auto" });
        focus?.focus({ preventScroll: target.scrollId !== null });
      }),
    );
  }, [shownDay]);
  const unsaved = Object.values(entries).some((entry) => entry.text.trim() !== "" || entry.scrap.trim() !== "" || entry.left.trim() !== "" || entry.pickupSaved);
  useHoldReload(unsaved, "today's stops", () => (draftsRead.current ? scope.write(DRAFTS, JSON.stringify(entriesToKeep(entries))) : undefined));
  useEffect(() => {
    if (!unsaved) return;
    const warn = (e: BeforeUnloadEvent) => !isReloading() && e.preventDefault();
    addEventListener("beforeunload", warn);
    return () => removeEventListener("beforeunload", warn);
  }, [unsaved]);
  const focusedRefusal = useRef<string | null>(null);
  const statusRefusal = status?.entry ? queued.find((entry) => entry.id === status.entry && entry.state === "failed" && isChange(entry)) : undefined;
  useEffect(() => {
    if (!statusRefusal || focusedRefusal.current === statusRefusal.id) return;
    focusedRefusal.current = statusRefusal.id;
    // Move the focus only from the page body: the driver can type at a different stop.
    const lost = !document.activeElement || document.activeElement === document.body;
    const button = lost ? document.getElementById(`fix-${statusRefusal.id}`) : null;
    console.info(`today: change ${statusRefusal.id} is not saved, focus ${button ? `fix-${statusRefusal.id}` : "stays"}`);
    button?.focus();
  }, [statusRefusal]);
  const toldHeld = useRef(new Set<string>());
  useEffect(() => {
    if (typeof shownDay !== "object") return;
    for (const route of shownDay.routes)
      for (const stop of route.stops) {
        const refused = states.get(stop.id);
        if (stop.state !== "planned" || refused?.state !== "failed" || refused.kind !== "stop" || refused.body.state !== "done") continue;
        const pickup = stopPickup(queued, route.id, stop.account_id);
        if (!pickupHeld(pickup) || toldHeld.current.has(refused.id)) continue;
        toldHeld.current.add(refused.id);
        console.info(`today: refused stop ${refused.id} opens with pickup ${pickup.id} ${pickup.state}, field ${refused.field ?? "none"}; only Mark it done is left`);
      }
    for (const route of shownDay.routes)
      for (const stop of route.stops) {
        const saved = stop.state === "planned" ? stop.saved_pickup : null;
        if (!saved || toldHeld.current.has(`server:${stop.id}`)) continue;
        toldHeld.current.add(`server:${stop.id}`);
        console.info(`today: stop ${stop.id} opens with pickup ${saved.id} on the server; only Mark it done is left`);
      }
    for (const route of shownDay.routes)
      for (const stop of route.stops) {
        const pickup = stopPickup(queued, route.id, stop.account_id);
        if (pickup && otherPickupKept(pickup) && !toldHeld.current.has(`other:${pickup.id}`)) {
          toldHeld.current.add(`other:${pickup.id}`);
          console.warn(`today: pickup ${pickup.id} of stop ${stop.id} got 409, the server keeps a different pickup (${serverGallons(stop) ? "gallons known" : "gallons not in the day"}); the top line gives the server gallons`);
        }
        const refused = states.get(stop.id);
        if (refused && doneElsewhere(refused, stop) && !toldHeld.current.has(`elsewhere:${refused.id}`)) {
          toldHeld.current.add(`elsewhere:${refused.id}`);
          console.warn(`today: refused first entry ${refused.id} of stop ${stop.id} is not needed, the stop is ${stop.state} on the server; the card asks for Discard on Sync`);
        }
      }
  }, [shownDay, states, queued]);
  if (canView === false || found === "hidden") {
    return (
      <main data-testid="today-hidden">
        <h1 tabIndex={-1}>Route</h1>
        <p>Your role has no route to run.</p>
      </main>
    );
  }
  const entryOf = (id: string) => entries[id] ?? blank();
  /** The pickup of the stop that is on the phone or on the server: the stop form must not make a second one. */
  const heldPickupOf = (route: api.Route, stop: api.RouteStop) => {
    const pickup = stopPickup(queued, route.id, stop.account_id);
    return pickupHeld(pickup) ? pickup : undefined;
  };
  /** The pickup that the server has for a planned stop. A different phone or an empty cache must not make a second one. */
  const serverPickupOf = (stop: api.RouteStop) => (stop.state === "planned" ? (stop.saved_pickup ?? undefined) : undefined);
  /** The stop form values, with a held pickup shown as saved, also after a reload. */
  const stopEntryOf = (route: api.Route, stop: api.RouteStop): Entry => {
    const entry = entryOf(stop.id);
    if (entry.changing || entry.pickupSaved) return entry;
    const held = heldPickupOf(route, stop);
    const saved = serverPickupOf(stop);
    if (saved && (!held || held.state === "failed")) return { ...entry, ...savedGallons(saved), pickupSaved: true };
    return held ? { ...entry, ...heldGallons(held), pickupSaved: true } : entry;
  };
  const change = (id: string, fields: Partial<Entry>) => setEntries((all) => ({ ...all, [id]: { ...(all[id] ?? blank()), ...fields } }));
  const failed = (id: string, e: unknown, thing: string) => {
    if (e instanceof Error && e.message === NO_STORAGE) {
      change(id, { error: NO_STORAGE, errorAt: null });
      return;
    }
    const saved = saveFailure(e, thing);
    change(id, { error: notSavedText(saved.text), errorAt: null });
    if (!saved.ok && saved.session) setStatus({ ok: false, text: "Your session ended.", session: true });
    if (e instanceof api.ApiError && reloadsDay(e.status)) void reloadDay(notSavedText(saved.text));
  };
  const reloadDay = async (text: string) => {
    try {
      const asked = Date.now();
      const day = await api.routeDay(await user.getIdToken());
      if (!live.current) return;
      editFound(day);
      setDayAt(asked);
      setStatus({ ok: false, text });
    } catch {
      // The error stays at the stop; the driver can load the page again.
    }
  };
  const wrong = (id: string, at: OutcomeField, error: string) => {
    change(id, { error, errorAt: at });
    const field = at === "gallons" || at === "scrap" ? `${id}-${at}` : `${at}-${id}`;
    requestAnimationFrame(() => document.getElementById(field)?.focus());
  };
  /** Leave the outcome form. A reopened refused entry gets its "Change this entry" button and the focus back. */
  const back = (stopId: string, fixing: string | undefined) => {
    change(stopId, { outcome: null, choice: "", other: "", left: "", error: null, errorAt: null, fixing: undefined });
    requestAnimationFrame(() => document.getElementById(fixing ? `fix-${fixing}` : `save-${stopId}`)?.focus());
  };
  /** In a change, go from an outcome to Done in one tap: the gallons fields and the save stay (SO-1057). */
  const toDone = (stopId: string) => {
    change(stopId, { outcome: null, choice: "", other: "", left: "", error: null, errorAt: null });
    console.info(`today: the change of stop ${stopId} goes to done`);
    requestAnimationFrame(() => document.getElementById(`${stopId}-gallons`)?.focus());
  };
  const keepValues = (stopId: string, refusedId: string) => {
    change(stopId, { askReplace: false });
    requestAnimationFrame(() => document.getElementById(`fix-${refusedId}`)?.focus());
  };
  /** Put a refused stop entry back in the stop form and mark the field that the server named. Ask first over typed values. */
  const reopenStop = (stop: api.RouteStop, refused: OutboxEntry, replace = false) => {
    const entry = entryOf(stop.id);
    if (!replace && entry.fixing !== refused.id && stopDraft(entry)) {
      console.info(`today: refused ${refused.kind} ${refused.id} waits, the stop form has values that are not saved`);
      change(stop.id, { askReplace: true });
      requestAnimationFrame(() => document.getElementById(`draft-keep-${stop.id}`)?.focus());
      return;
    }
    const view = stopReopen(refused, stopPickup(queued, refused.route_id, stop.account_id));
    change(stop.id, { ...view.fields, error: view.error, errorAt: view.at, fixing: refused.id, askReplace: false, askNoScrap: false });
    console.info(`today: refused ${refused.kind} ${refused.id} is in the form, field ${view.at ?? "none"}`);
    // When the refusal names no field, the save button gets the focus. Thus the focus does not go to the page body.
    const focusId = view.focusId ?? (view.fields.outcome ? `confirm-${stop.id}` : `save-${stop.id}`);
    requestAnimationFrame(() => document.getElementById(focusId)?.focus());
  };
  const keep = async (made: NewEntry): Promise<OutboxEntry> => {
    const kept = await enqueue(scope, made);
    saves.current += 1;
    return kept;
  };
  const finish = (route: api.Route, id: string, entry: OutboxEntry, text: string, changed = false) => {
    scrollTo.current = afterSave(route, id, changed);
    console.info(`today: stop ${id} saved as ${entry.id}, the card collapses, focus ${scrollTo.current.focusId}`);
    setEntries((all) => {
      const rest = { ...all };
      delete rest[id];
      return rest;
    });
    setStatus({ ok: true, text, entry: entry.id });
  };
  const ensureLoad = async (route: api.Route, today: string): Promise<api.Load | undefined> =>
    openLoad(shownLoads, canLoad, route.truck, today, async (entry) => {
      const made = loadEntry({ id: crypto.randomUUID(), ...entry }, `New load, truck ${route.truck}`);
      const kept = await keep(made);
      return withLocalLoads(loads, [kept]).find((load) => load.id === made.id) ?? null;
    });
  const savePickup = async (day: api.RouteDay, route: api.Route, stop: api.RouteStop, pickupId: string, gallons: Gallons) => {
    const load = await ensureLoad(route, day.today);
    const body = { id: pickupId, at: day.today, raw_gal: gallons.raw, scrap_gal: gallons.scrap, route_stop_id: stop.id, ...(load ? { load_id: load.id } : {}) };
    await keep(pickupEntry(stop.account_id, body, `Pickup at ${stop.number}, ${gallonsText(gallons)}`, route.id));
    if (live.current) change(stop.id, { pickupSaved: true });
  };
  const asksScrap = (stop: api.RouteStop, gallons: Gallons | null, noScrap: boolean) => {
    if (noScrap || !gallons || !asksNoScrap(gallons.raw, gallons.scrap)) return false;
    change(stop.id, { askNoScrap: true, error: null, errorAt: null });
    requestAnimationFrame(() => document.getElementById(`${stop.id}-no-scrap-yes`)?.focus());
    return true;
  };
  /** The capacity warning of the account page: a second Save with the same gallons saves them. */
  const asksCapacity = (stop: api.RouteStop, entry: Entry, gallons: Gallons | null) => {
    const warning = gallons ? capacityWarning(gallons, stop.capacity_gal ?? 0) : "";
    if (!gallons || !warning || entry.overCapacity === capacityKey(gallons)) return false;
    console.info(`today: stop ${stop.id} gallons are over the container size, the driver must save again`);
    change(stop.id, { overCapacity: capacityKey(gallons), error: warning, errorAt: "gallons" });
    requestAnimationFrame(() => document.getElementById(`${stop.id}-gallons`)?.focus());
    return true;
  };
  /** Rule D-19: the place of a first stop entry on a phone. Null: the driver must answer the question first. */
  const placeOf = async (day: api.RouteDay, stop: api.RouteStop, outcome: Outcome | null, noScrap: boolean): Promise<{ place: api.Place | null } | null> => {
    const phone = isPhone();
    const pin = stopPin(stop.tank, stop.door);
    const on = day.place_check_on !== false;
    if (!needsCheck(phone, pin, on)) {
      console.info(`today: stop ${stop.id} has no place check, ${!on ? "the check is off" : phone ? "the stop has no pin" : "not a phone"}`);
      return { place: null };
    }
    const started = performance.now();
    const decision = decidePlace(phone, pin, await readFix(), day.place_far_m ?? FAR_M);
    const check = decision.place?.check ?? "none";
    console.info(`today: stop ${stop.id} place check ${check}, distance ${bucket(decision.place?.distance_m)}, ${Math.round(performance.now() - started)} ms`);
    if (!decision.ask) return { place: decision.place };
    if (!live.current) return null;
    change(stop.id, { askPlace: { place: decision.place, outcome, noScrap, hint: decision.ask === "denied" && takeHint() } });
    requestAnimationFrame(() => document.getElementById(`place-yes-${stop.id}`)?.focus());
    return null;
  };
  const placeYes = (day: api.RouteDay, route: api.Route, stop: api.RouteStop, ask: NonNullable<Entry["askPlace"]>) => {
    change(stop.id, { askPlace: undefined });
    console.info(`today: stop ${stop.id} place ${ask.place.check} confirmed by the driver`);
    void (ask.outcome ? closeStop(day, route, stop, ask.outcome, ask.noScrap, ask.place) : saveStop(day, route, stop, ask.noScrap, ask.place));
  };
  const placeNo = (stop: api.RouteStop, ask: NonNullable<Entry["askPlace"]>) => {
    change(stop.id, { askPlace: undefined });
    console.info(`today: stop ${stop.id} place ${ask.place.check}, the driver cancelled, nothing is saved`);
    requestAnimationFrame(() => document.getElementById(ask.outcome ? `confirm-${stop.id}` : `save-${stop.id}`)?.focus());
  };
  const saveStop = async (day: api.RouteDay, route: api.Route, stop: api.RouteStop, noScrap = false, place?: api.Place | null) => {
    const entry = stopEntryOf(route, stop);
    const gallons = entry.pickupSaved ? null : parseGallons(entry.text, entry.scrap);
    if (gallons && "error" in gallons) {
      wrong(stop.id, gallons.field === "total" ? "gallons" : "scrap", gallons.error);
      return;
    }
    const confirmed = gallons !== null && entry.overCapacity === capacityKey(gallons);
    if (busy || asksScrap(stop, gallons, noScrap || confirmed) || asksCapacity(stop, entry, gallons)) return;
    setBusy(stop.id);
    setStatus(null);
    change(stop.id, { error: null, errorAt: null, askNoScrap: false, askPlace: undefined });
    try {
      const where = place !== undefined || entry.changing ? { place: place ?? null } : await placeOf(day, stop, null, true);
      if (!where) return;
      if (entry.changing && gallons) {
        const load = stop.raw_gal == null ? await ensureLoad(route, day.today) : undefined;
        const body = { state: "done" as const, raw_gal: gallons.raw, scrap_gal: gallons.scrap, ...(load ? { load_id: load.id } : {}) };
        await sendChange(route, stop, entry.changing, body, `${stop.number} is changed: done with ${gallonsText(gallons)}.`);
        return;
      }
      if (gallons) await savePickup(day, route, stop, entry.pickupId, gallons);
      const shown = gallons ? ` with ${gallonsText(gallons)}` : "";
      const text = `${stop.number} is done${shown}.`;
      const kept = await keep(stopEntry(route.id, stop.id, { state: "done", at: new Date().toISOString(), ...(where.place ? { place: where.place } : {}) }, text));
      if (live.current) finish(route, stop.id, kept, text);
    } catch (e) {
      if (e instanceof Error && e.message === SECOND_PICKUP) {
        const held = stopPickup(await outboxEntries(scope), route.id, stop.account_id);
        if (live.current) change(stop.id, { pickupSaved: true, ...(held ? heldGallons(held) : {}), error: secondPickupText(held), errorAt: null });
        // Two frames: the button is disabled until the busy state clears.
        requestAnimationFrame(() => requestAnimationFrame(() => document.getElementById(`save-${stop.id}`)?.focus()));
      } else if (live.current) failed(stop.id, e, "The stop");
    } finally {
      if (live.current) setBusy(null);
    }
  };
  const closeStop = async (day: api.RouteDay, route: api.Route, stop: api.RouteStop, outcome: Outcome, noScrap = false, place?: api.Place | null) => {
    const entry = stopEntryOf(route, stop);
    const input = outcomeInput(outcome, entry);
    if ("error" in input) {
      wrong(stop.id, input.at, input.error);
      return;
    }
    const { reason, left, taken } = input;
    const confirmed = taken !== null && entry.overCapacity === capacityKey(taken);
    if (busy || asksScrap(stop, taken, noScrap || confirmed) || asksCapacity(stop, entry, taken)) return;
    setBusy(stop.id);
    setStatus(null);
    change(stop.id, { error: null, errorAt: null, askNoScrap: false, askPlace: undefined });
    try {
      // A skip does not say that the driver was at the stop, so it has no place check.
      const checked = place !== undefined || entry.changing || outcome === "skipped";
      const where = checked ? { place: place ?? null } : await placeOf(day, stop, outcome, true);
      if (!where) return;
      // The API writes the pickup of a partly emptied stop with the stop visit.
      const load = taken && !(entry.changing && stop.raw_gal != null) ? await ensureLoad(route, day.today) : undefined;
      const pumped = taken ? { raw_gal: taken.raw, scrap_gal: taken.scrap, ...(load ? { load_id: load.id } : {}) } : {};
      const visit = { state: outcome, reason, ...(left !== null ? { gallons_left: left } : {}), ...pumped, at: new Date().toISOString() };
      const placed = where.place ? { ...visit, place: where.place } : visit;
      const shown = outcomeSaved(outcome, reason, left, taken);
      if (entry.changing) {
        await sendChange(route, stop, entry.changing, visit, `${stop.number} is changed: ${shown}.`);
        return;
      }
      const text = `${stop.number} is ${shown}.`;
      const kept = await keep(stopEntry(route.id, stop.id, placed, text));
      if (live.current) finish(route, stop.id, kept, text);
    } catch (e) {
      if (live.current) failed(stop.id, e, outcome === "skipped" ? "The skip" : "The stop");
    } finally {
      if (live.current) setBusy(null);
    }
  };
  /** Keep a change of a saved stop result as one outbox entry with its own id (SO-1033). */
  const sendChange = async (route: api.Route, stop: api.RouteStop, changeId: string, body: Omit<Parameters<typeof changeEntry>[3], "at">, text: string) => {
    const kept = await keep(changeEntry(route.id, stop.id, changeId, { ...body, at: new Date().toISOString() }, text));
    console.info(`today: change ${changeId} of stop ${stop.id} is kept, ${stop.state} -> ${body.state}`);
    if (!live.current) return;
    setChanged((all) => ({ ...all, [stop.id]: { text, entry: kept.id } }));
    finish(route, stop.id, kept, text, true);
  };
  const startChange = (stop: api.RouteStop, refused?: OutboxEntry) => {
    if (busy) return;
    setChanged((all) => Object.fromEntries(Object.entries(all).filter(([id]) => id !== stop.id)));
    const view = refused ? changeReopen(refused) : null;
    const fields = view ? view.fields : changeFields(stop);
    change(stop.id, { ...fields, pickupSaved: false, changing: crypto.randomUUID(), error: view?.error ?? null, errorAt: view?.at ?? null, askNoScrap: false, fixing: refused?.id });
    console.info(`today: the change form of stop ${stop.id} (${stop.state}) opens${refused ? ` with refused change ${refused.id}` : ""}`);
    const focusId = view?.focusId ?? (fields.outcome ? `${OUTCOMES[fields.outcome].reads ? "left" : "reason"}-${stop.id}` : `${stop.id}-gallons`);
    requestAnimationFrame(() => document.getElementById(focusId)?.focus());
  };
  const cancelChange = (stop: api.RouteStop) => {
    const fixing = entryOf(stop.id).fixing;
    setEntries((all) => {
      const rest = { ...all };
      delete rest[stop.id];
      return rest;
    });
    console.info(`today: the change of stop ${stop.id} is cancelled, nothing is sent`);
    requestAnimationFrame(() => (document.getElementById(`change-${stop.id}`) ?? document.getElementById(`fix-${fixing}`))?.focus());
  };
  const newLoad = async (route: api.Route, today: string) => {
    if (busy) return;
    setBusy(route.id);
    setStatus(null);
    try {
      const kept = await keep(loadEntry({ id: crypto.randomUUID(), day: today, truck: route.truck }, `New load, truck ${route.truck}`));
      if (live.current) setStatus({ ok: true, text: `a new load for truck ${route.truck}. The next pickups go on it.`, entry: kept.id });
    } catch (e) {
      if (!live.current) return;
      const saved = saveFailure(e, "The load");
      const text = e instanceof Error && e.message === NO_STORAGE ? NO_STORAGE : notSavedText(saved.text);
      setStatus({ ok: false, text, session: !saved.ok && saved.session });
    } finally {
      if (live.current) setBusy(null);
    }
  };
  const showPinNote = (stopId: string, note: PinNote) => {
    setPinNotes((all) => ({ ...all, [stopId]: note }));
    requestAnimationFrame(() => document.getElementById(`pin-note-${stopId}`)?.focus());
  };
  const dropPinNote = (stopId: string) =>
    setPinNotes((all) => {
      const rest = { ...all };
      delete rest[stopId];
      return rest;
    });
  // Undo stays until the next pin change on the stop; after a reload, Pin history is the way back.
  const keepPinUndo = (stopId: string, undo: api.LocationChange | null) =>
    setPinUndos((all) => {
      const rest = { ...all };
      if (undo) rest[stopId] = undo;
      else delete rest[stopId];
      return rest;
    });
  const pinFailed = (stop: api.RouteStop, e: unknown) => {
    const saved = saveFailure(e, "The container pin");
    if (!saved.ok && saved.session) {
      dropPinNote(stop.id);
      setStatus({ ok: false, text: "Your session ended.", session: true });
    } else showPinNote(stop.id, { ok: false, text: notSavedText(saved.text) });
  };
  const pinHere = (stop: api.RouteStop) => {
    if (busy) return;
    if (!("geolocation" in navigator)) {
      showPinNote(stop.id, { ok: false, text: "This phone cannot give a GPS position." });
      return;
    }
    setBusy(stop.id);
    dropPinNote(stop.id);
    const before = tankUndo(stop.tank);
    navigator.geolocation.getCurrentPosition(
      async (fix) => {
        const at = { lat: fix.coords.latitude, lng: fix.coords.longitude, source: "gps" as const, accuracy_m: fix.coords.accuracy };
        try {
          const token = await user.getIdToken();
          await api.setAccountLocation(token, stop.account_id, { point: "tank", ...at });
          if (!live.current) return;
          editFound((day) => (typeof day === "object" ? withTankPin(day, stop.account_id, at) : day));
          keepPinUndo(stop.id, before);
          showPinNote(stop.id, { ok: true, text: `Saved: the container pin of ${stop.number} is here, ${accuracyText(fix.coords.accuracy)}.` });
        } catch (e) {
          if (live.current) pinFailed(stop, e);
        } finally {
          if (live.current) setBusy(null);
        }
      },
      (error) => {
        setBusy(null);
        const denied = error.code === error.PERMISSION_DENIED;
        showPinNote(stop.id, { ok: false, text: denied ? "Allow the location for this site, then try again." : "No GPS position. Go outside and try again." });
      },
      { enableHighAccuracy: true, timeout: 20_000, maximumAge: 0 },
    );
  };
  const movePin = async (stop: api.RouteStop, change: api.LocationChange, text: string, undo: api.LocationChange | null) => {
    if (busy) return;
    setBusy(`undo-${stop.id}`);
    try {
      await api.setAccountLocation(await user.getIdToken(), stop.account_id, change);
      if (!live.current) return;
      const back = "reset" in change ? null : { lat: change.lat, lng: change.lng, source: change.source, accuracy_m: change.accuracy_m };
      editFound((day) => (typeof day === "object" ? withTankPin(day, stop.account_id, back) : day));
      keepPinUndo(stop.id, undo);
      showPinNote(stop.id, { ok: true, text });
    } catch (e) {
      if (live.current) pinFailed(stop, e);
    } finally {
      if (live.current) setBusy(null);
    }
  };
  const undoPin = (stop: api.RouteStop, undo: api.LocationChange) => void movePin(stop, undo, `The container pin of ${stop.number} is back as it was.`, null);
  const restorePin = (stop: api.RouteStop, change: api.LocationChange, entry: api.PinEntry) =>
    void movePin(stop, change, restoredText(entry, org.time_zone, `the container pin of ${stop.number}`), tankUndo(stop.tank));
  const errorId = (stop: api.RouteStop) => `error-${stop.id}`;
  const fieldError = (stop: api.RouteStop, entry: Entry, at: OutcomeField | null) =>
    entry.error && (entry.errorAt ?? null) === at ? (
      <p id={errorId(stop)} role="alert" className="error" data-testid="today-error">
        {entry.error}
      </p>
    ) : null;
  const invalid = (stop: api.RouteStop, entry: Entry, at: OutcomeField) =>
    entry.error && entry.errorAt === at ? { "aria-invalid": true, "aria-describedby": errorId(stop) } : {};
  const placeAsk = (day: api.RouteDay, route: api.Route, stop: api.RouteStop, ask: NonNullable<Entry["askPlace"]>) => (
    <PlaceConfirm
      id={stop.id}
      place={ask.place}
      farM={day.place_far_m ?? FAR_M}
      hint={ask.hint}
      disabled={busy !== null}
      onYes={() => placeYes(day, route, stop, ask)}
      onNo={() => placeNo(stop, ask)}
    />
  );
  const outcomeForm = (day: api.RouteDay, route: api.Route, stop: api.RouteStop, entry: Entry, outcome: Outcome) => {
    const text = OUTCOMES[outcome];
    return (
      <form
        className="stop-skip"
        data-testid="today-outcome-form"
        data-outcome={outcome}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void closeStop(day, route, stop, outcome);
        }}
      >
        {text.reads && (
          <label>
            Gallons left in the container
            <input
              id={`left-${stop.id}`}
              data-testid="today-left"
              value={entry.left}
              inputMode="decimal"
              placeholder="300"
              autoComplete="off"
              {...invalid(stop, entry, "left")}
              onChange={(e) => change(stop.id, { left: e.target.value, error: null })}
            />
          </label>
        )}
        {fieldError(stop, entry, "left")}
        <label>
          {text.question}
          <select
            id={`reason-${stop.id}`}
            data-testid="today-outcome-reason"
            value={entry.choice}
            {...invalid(stop, entry, "reason")}
            onChange={(e) => change(stop.id, { choice: e.target.value, error: null })}
          >
            <option value="">Choose…</option>
            {text.reasons.map((reason) => (
              <option key={reason} value={reason}>
                {reason}
              </option>
            ))}
          </select>
        </label>
        {fieldError(stop, entry, "reason")}
        {entry.choice === "Other" && (
          <label>
            Reason
            <input
              id={`other-${stop.id}`}
              data-testid="today-outcome-other"
              value={entry.other}
              {...invalid(stop, entry, "other")}
              onChange={(e) => change(stop.id, { other: e.target.value, error: null })}
            />
          </label>
        )}
        {fieldError(stop, entry, "other")}
        {outcome === "partial" && (
          <p className="sub">{entry.pickupSaved ? "The pickup of the pumped gallons is saved." : "Type the total and the scrap gallons that you pumped above. The save records the pickup too."}</p>
        )}
        {fieldError(stop, entry, null)}
        <button id={`confirm-${stop.id}`} type="submit" className={text.reads ? "primary" : "danger"} data-testid="today-outcome-confirm" disabled={busy !== null}>
          {entry.changing ? changeButton(outcome) : text.confirm}
        </button>
        {!entry.changing && (
          <>
            {" "}
            <button type="button" onClick={() => back(stop.id, entry.fixing)}>
              Back
            </button>
          </>
        )}
      </form>
    );
  };
  const stopCard = (day: api.RouteDay, route: api.Route, stop: api.RouteStop, open: boolean) => {
    const entry = stopEntryOf(route, stop);
    const parsed = entry.text.trim() ? parseGallons(entry.text, entry.scrap) : null;
    const tel = telHref(stop.phone);
    const changeable = canChangeStop(day, route, stop, clockDay);
    const changing = changeable && entry.changing !== undefined;
    const runs = (open && stop.state === "planned") || changing;
    const visited = stop.state !== "planned" && !changing;
    const photos = stopPhotoCount(stop.photos ?? 0, dayAt, queued, stop.account_id, stop.id);
    const sync = states.get(stop.id);
    const doneHeld = runs && !changing && sync?.state === "failed" && sync.kind === "stop" && sync.body.state === "done" && (heldPickupOf(route, stop) !== undefined || serverPickupOf(stop) !== undefined);
    const serverHeld = heldPickupOf(route, stop) === undefined ? serverPickupOf(stop) : undefined;
    const accountHref = `${base}/accounts/${stop.account_id}`;
    const name = (
      <>
        <span className="account-number">{stop.number}</span> {stop.name} <SourceTag tag={stop.data_source} />
      </>
    );
    const heading = (
      <h3 id={`stop-${stop.id}`} tabIndex={-1}>
        <span className="stop-order">{stop.position}</span>{" "}
        <a href={accountHref} onClick={go}>
          {name}
        </a>
        {stop.off_route && <> <OffRouteMark truck={route.truck} /></>}
        {stop.requested && <> <RequestedMark /></>}
      </h3>
    );
    const photoCount = (
      <p className="sub" data-testid="today-photos">
        Photos: {photos}
      </p>
    );
    const photoAdd = (
      <PhotoAdd
        accountId={stop.account_id}
        stopId={stop.id}
        user={user}
        auth={auth}
        testId="today-photo"
        keep={{ scope, routeId: route.id, what: `${stop.number} ${stop.name}`, all: queued }}
        orderHref={(id) => `${base}/accounts/${stop.account_id}?photo=${id}`}
      />
    );
    const navigate = navigationUrl(navTarget(stop));
    const pinNote = pinNotes[stop.id];
    const pinUndo = pinUndos[stop.id];
    const facts = (
      <>
        {stop.address && <p className="stop-address">{stop.address}</p>}
        {(navigate || stop.can_pin) && (
          <div className="stop-tools">
            {navigate && (
              <a className="button" href={navigate} target="_blank" rel="noopener noreferrer" data-testid="today-navigate">
                Navigate
              </a>
            )}
            {stop.can_pin && (
              <button type="button" data-testid="today-pin" disabled={busy !== null} onClick={() => pinHere(stop)}>
                {busy === stop.id ? "Finding your position…" : "Set container pin here"}
              </button>
            )}
          </div>
        )}
        {(pinNote || pinUndo) && (
          <p id={`pin-note-${stop.id}`} tabIndex={-1} role="status" data-testid="today-pin-status" className={pinNote && !pinNote.ok ? "error" : undefined}>
            {pinNote?.text}
            {pinUndo && (
              <>
                {" "}
                <button type="button" data-testid="today-pin-undo" disabled={busy !== null} onClick={() => undoPin(stop, pinUndo)}>
                  Undo
                </button>
              </>
            )}
          </p>
        )}
        {stop.can_pin && (
          <PinHistory
            accountId={stop.account_id}
            tank={stop.tank}
            canRestore
            disabled={busy !== null}
            timeZone={org.time_zone}
            user={user}
            onRestore={(change, entry) => restorePin(stop, change, entry)}
          />
        )}
        <p className="sub">
          Window: {windowText(stop.window)}
          {plannedText(stop)}
        </p>
        {stop.blackouts && stop.blackouts.length > 0 && (
          <p className="stop-note" data-testid="today-blackouts">
            Do not arrive: {stop.blackouts.map((item) => `${item.start}–${item.end}`).join(", ")}
          </p>
        )}
        {stop.access_note && (
          <p className="stop-note" data-testid="today-access">
            Access: {stop.access_note}
          </p>
        )}
        {outcomeText(stop) && (
          <p className="sub" data-testid="today-outcome">
            {outcomeText(stop)}
          </p>
        )}
      </>
    );
    if (visited) {
      const saved = savedValues(stop);
      const refusedChange = sync?.state === "failed" && isChange(sync) ? sync : undefined;
      return (
        <li key={stop.id} className="stop-card visited" data-testid="today-stop" data-number={stop.number} data-state={stop.state} aria-labelledby={`stop-${stop.id}`}>
          <details>
            <summary>
              <h3 id={`stop-${stop.id}`}>
                <span className="stop-order">{stop.position}</span> {name}
                <span className={`chip-state ${stop.state}`}>{ROUTE_STATE_TEXT[stop.state]}</span>
                {sync && sync.state !== "failed" && (
                  <span className={`sync-state ${sync.state}`} data-testid="today-sync" data-state={sync.state}>
                    {stateText(sync)}
                  </span>
                )}
                {saved && <span data-testid="today-saved-values"> · {saved}</span>}
                <span className="stop-photos">
                  {" · "}
                  <span data-testid="today-photos">Photos: {photos}</span>
                </span>
              </h3>
            </summary>
            <p>
              <a href={accountHref} onClick={go}>
                Open the account {stop.number}
              </a>
            </p>
            {facts}
            {photoAdd}
          </details>
          {changed[stop.id] && changeResultShown(queued.find((kept) => kept.id === changed[stop.id].entry)) && (
            <p className="sub" data-testid="today-change-result">
              {changed[stop.id].text}
            </p>
          )}
          {refusedChange && <NotSaved entry={refusedChange} base={base} button={changeButton(entryOutcome(refusedChange))} change onChange={changeable ? () => startChange(stop, refusedChange) : undefined} />}
          {sync && stop.state !== "planned" && doneElsewhere(sync, stop) && (
            <p className="sub" data-testid="today-done-elsewhere">
              {doneElsewhereText(stop.number, stop.state, "today")}{" "}
              <a href={`${base}/sync`} onClick={go}>
                Open Sync
              </a>
            </p>
          )}
          {changeable && refusedChange && canChange(refusedChange.reason) ? null : changeable ? (
            <p className="stop-change">
              <button id={`change-${stop.id}`} type="button" data-testid="today-change" aria-label={`Change the entry of ${stop.number}`} disabled={busy !== null} onClick={() => startChange(stop)}>
                Change
              </button>
            </p>
          ) : (
            route.status !== "cancelled" &&
            routeDayOver(day, clockDay) && (
              <p className="sub" data-testid="today-change-office">
                {ASK_OFFICE}
              </p>
            )
          )}
        </li>
      );
    }
    return (
      <li
        key={stop.id}
        className={`stop-card${runs ? "" : " visited"}`}
        data-testid="today-stop"
        data-number={stop.number}
        data-state={stop.state}
        aria-labelledby={`stop-${stop.id}`}
      >
        {heading}
        {changing && (
          <p className="sub" data-testid="today-changing">
            {CHANGING_TEXT}
          </p>
        )}
        {facts}
        {sync?.state === "failed" && (
          <NotSaved
            entry={sync}
            base={base}
            button={doneHeld ? "Mark it done" : sync.kind === "stop" ? refusedButton(sync) : "Save"}
            pickupSaved={doneHeld}
            saved={serverGallons(stop)}
            onChange={runs && !changing && sync.kind === "stop" && entry.fixing !== sync.id && !doneHeld ? () => reopenStop(stop, sync) : undefined}
          />
        )}
        {sync?.state === "failed" && entry.askReplace && (
          <p
            className="confirm"
            role="group"
            aria-label="Change the refused entry"
            data-testid="today-draft-confirm"
            onKeyDown={(e) => {
              if (e.key === "Escape") keepValues(stop.id, sync.id);
            }}
          >
            <span aria-hidden="true">⚠ </span>
            {DRAFT_ASK}{" "}
            <button type="button" data-testid="today-draft-change" onClick={() => reopenStop(stop, sync, true)}>
              {CHANGE_LABEL}
            </button>{" "}
            <button id={`draft-keep-${stop.id}`} type="button" className="primary" data-testid="today-draft-keep" onClick={() => keepValues(stop.id, sync.id)}>
              {DRAFT_KEEP}
            </button>
          </p>
        )}
        {runs && (
          <>
            {pickupSavedShown(entry.pickupSaved, doneHeld, sync) && (
              <p className="sub" data-testid="today-pickup-saved">
                {serverHeld ? serverPickupText(serverHeld) : pickupSavedText(null)}
              </p>
            )}
            <form
              className="stop-entry"
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                void (entry.outcome ? closeStop(day, route, stop, entry.outcome) : saveStop(day, route, stop));
              }}
            >
              <GallonsFields
                id={stop.id}
                testId="today"
                word="gallons"
                total={entry.text}
                scrap={entry.scrap}
                onTotal={(text) => change(stop.id, { text, error: null, askNoScrap: false, askPlace: undefined })}
                onScrap={(scrap) => change(stop.id, { scrap, error: null, askNoScrap: false, askPlace: undefined })}
                disabled={entry.pickupSaved}
                totalAria={{
                  "aria-invalid": Boolean(entry.error && entry.errorAt === "gallons") || undefined,
                  "aria-describedby": entry.error && entry.errorAt === "gallons" ? `net-${stop.id} ${errorId(stop)}` : `net-${stop.id}`,
                }}
                scrapAria={invalid(stop, entry, "scrap")}
              />
              <span id={`net-${stop.id}`} className="sub" data-testid="today-net">
                {parsed && !("error" in parsed) ? netText(parsed) : "Net gallons: total less scrap."}
              </span>
              {!entry.outcome && (
                <button id={`save-${stop.id}`} type="submit" className="primary" data-testid="today-save" disabled={busy !== null}>
                  {changing ? CHANGE_DONE : entry.pickupSaved ? "Mark it done" : "Save and mark done"}
                </button>
              )}
            </form>
            {entry.askNoScrap && (
              <NoScrapConfirm
                id={stop.id}
                testId="today"
                disabled={busy !== null}
                onYes={() => void (entry.outcome ? closeStop(day, route, stop, entry.outcome, true) : saveStop(day, route, stop, true))}
                onChange={() => {
                  change(stop.id, { askNoScrap: false });
                  requestAnimationFrame(() => document.getElementById(`${stop.id}-scrap`)?.focus());
                }}
              />
            )}
            {entry.askPlace && !entry.askPlace.outcome && placeAsk(day, route, stop, entry.askPlace)}
            {fieldError(stop, entry, "gallons")}
            {fieldError(stop, entry, "scrap")}
            {!entry.outcome && fieldError(stop, entry, null)}
            <div className="stop-tools">
              {tel && (
                <a className="button" href={tel} data-testid="today-call">
                  Call
                </a>
              )}
              <a className="button" href={`${base}/accounts/${stop.account_id}?from=route`} onClick={go} data-testid="today-log-call">
                Log a call
              </a>
              {changing && entry.outcome && (
                <button type="button" className="secondary" data-testid="today-change-done" disabled={busy !== null} onClick={() => toDone(stop.id)}>
                  Done
                </button>
              )}
              {(!entry.outcome || changing) &&
                OUTCOME_ORDER.map((outcome) => (
                  <button
                    key={outcome}
                    type="button"
                    className="secondary"
                    data-testid={OUTCOME_TEST_ID[outcome]}
                    aria-pressed={changing ? entry.outcome === outcome : undefined}
                    disabled={busy !== null || (entry.pickupSaved && outcome !== "partial")}
                    onClick={() => {
                      if (entry.outcome !== outcome) change(stop.id, { outcome, choice: "", other: "", error: null, errorAt: null });
                      requestAnimationFrame(() => document.getElementById(`${OUTCOMES[outcome].reads ? "left" : "reason"}-${stop.id}`)?.focus());
                    }}
                  >
                    {OUTCOMES[outcome].button}
                  </button>
                ))}
              {changing && (
                <button type="button" data-testid="today-change-cancel" disabled={busy !== null} onClick={() => cancelChange(stop)}>
                  Cancel the change
                </button>
              )}
            </div>
            {entry.outcome && outcomeForm(day, route, stop, entry, entry.outcome)}
            {entry.askPlace?.outcome && placeAsk(day, route, stop, entry.askPlace)}
          </>
        )}
        {photoCount}
        {photoAdd}
      </li>
    );
  };
  const routeCard = (day: api.RouteDay, route: api.Route) => {
    const items = day.check_items ?? [];
    const checking = route.can_check === true;
    const waiting = items.length > 0 && route.status === "planned" && !route.started_at;
    const open = (route.status === "planned" || route.status === "started") && !waiting;
    const finishing = route.mine && route.status === "done" && route.odometer_start != null && route.odometer_end == null;
    const started = async (start: api.RouteStart) => {
      const bad = start.results.filter((result) => !result.ok).length;
      const text = bad ? `Route started. ${bad} ${bad === 1 ? "item is" : "items are"} not OK; the dispatcher sees them.` : "Route started.";
      const kept = await keep(startEntry(route.id, { ...start, id: crypto.randomUUID(), at: new Date().toISOString() }, `Truck ${route.truck}: ${text}`));
      if (live.current) setStatus({ ok: true, text, entry: kept.id });
    };
    const finished = async (odometer: number) => {
      const kept = await keep(finishEntry(route.id, { odometer, at: new Date().toISOString() }, `Truck ${route.truck}: route finished.`));
      if (live.current) setStatus({ ok: true, text: "Route finished.", entry: kept.id });
    };
    const load = currentLoad(shownLoads, route.truck, day.today);
    const next = nextStop(route);
    return (
      <section
        key={route.id}
        className="today-route"
        data-testid="today-route"
        data-truck={route.truck}
        data-status={route.status}
        data-started={route.started_at ? "true" : "false"}
        aria-labelledby={`today-${route.id}`}
      >
        <h2 id={`today-${route.id}`}>Truck {route.truck}</h2>
        {[states.get(`start:${route.id}`), states.get(`finish:${route.id}`)].map(
          (entry) => entry?.state === "failed" && <NotSaved key={entry.id} entry={entry} base={base} />,
        )}
        {checking && <RouteStartForm route={route} items={items} save={started} />}
        {waiting && !checking && (
          <p className="sub" data-testid="today-check-waiting">
            {checkWaitingText(route.mine)}
          </p>
        )}
        <RouteCheckSummary route={route} />
        {finishing && <RouteFinishForm route={route} save={finished} />}
        <p className="sub" data-testid="today-progress">
          {progressText(route)}
          {next && (
            <>
              {" · next: "}
              <a
                href={`#stop-${next.id}`}
                data-testid="today-next"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToStop(next.id);
                }}
              >
                {next.number}
              </a>
            </>
          )}
        </p>
        {open && (
          <p className="today-load" data-testid="today-load">
            {load ? (load.code === "new" ? "New load (saved on phone)" : `Load ${load.code}`) : canLoad ? "No open load. The first save makes one." : "No open load."}{" "}
            {canLoad && (
              <button type="button" data-testid="today-new-load" disabled={busy !== null} onClick={() => void newLoad(route, day.today)}>
                New load
              </button>
            )}
          </p>
        )}
        {route.stops.length === 0 ? <p>No stops on this route yet.</p> : <ol id={`stops-${route.id}`} className="stop-list" tabIndex={-1}>{route.stops.map((stop) => stopCard(day, route, stop, open))}</ol>}
      </section>
    );
  };
  const shown = typeof shownDay === "object" ? shownDay : undefined;
  /** The card line of a refused first entry with a saved pickup (SO-1081, SO-1109). The top line gives the same text. */
  const statusPickupLine = (entry: OutboxEntry | undefined) => {
    if (entry?.state !== "failed" || entry.kind !== "stop" || isChange(entry)) return null;
    const route = shown?.routes.find((one) => one.id === entry.route_id);
    const stop = route?.stops.find((one) => one.id === entryStop(entry));
    if (!route || !stop) return null;
    if (entry.reason === FIELD_TEXT.stopPickup) return todayReason(entry.reason, serverGallons(stop));
    if (entry.body.state !== "done" || (heldPickupOf(route, stop) === undefined && serverPickupOf(stop) === undefined)) return null;
    return pickupSavedText(entry);
  };
  /** The text of a Done entry whose pickup the server refused for a pickup of a different phone (SO-1106). */
  const statusOtherPickup = (entry: OutboxEntry | undefined, text: string) => {
    if (!entry || entry.state === "failed" || entry.kind !== "stop" || isChange(entry) || entry.body.state !== "done") return text;
    const route = shown?.routes.find((one) => one.id === entry.route_id);
    const stop = route?.stops.find((one) => one.id === entryStop(entry));
    const pickup = route && stop ? stopPickup(queued, route.id, stop.account_id) : undefined;
    return stop && pickup && otherPickupKept(pickup) ? otherPickupText(stop.number, pickup, serverGallons(stop)) : text;
  };
  const statusEntry = status?.entry ? queued.find((entry) => entry.id === status.entry) : undefined;
  const statusText = !status ? null : !status.entry ? status.text : keptStatusText(statusOtherPickup(statusEntry, status.text), statusEntry, statusPickupLine(statusEntry));
  const mine = shown?.routes.filter((route) => route.mine && route.status !== "cancelled") ?? [];
  return (
    <main className="today-page" data-testid="today-page" data-today={shown?.today ?? ""}>
      <h1 tabIndex={-1}>Route</h1>
      <InstallNote />
      {found === undefined ? (
        <Skeleton />
      ) : found === "session" ? (
        <p role="alert" className="error">
          Your session ended.{" "}
          <button type="button" onClick={() => void signOut(auth)}>
            Sign in again
          </button>
        </p>
      ) : typeof found === "string" ? (
        <p role="alert" className="error">
          {found === "server" ? SERVER_PROBLEM : "Cannot reach the server. Check your connection."}{" "}
          <button type="button" onClick={tryAgain}>
            Try again
          </button>
        </p>
      ) : (
        <>
          <p className="sub" data-testid="today-day">
            {dayTitle(found.day, found.today)}
          </p>
          {offlineAt !== null && checking ? (
            <p className="offline-note" role="status" data-testid="today-checking">
              {checkingText(offlineAt, org.time_zone)}
            </p>
          ) : offlineAt !== null ? (
            <p className="offline-note" data-testid="today-offline">
              {offlineText(offlineAt, org.time_zone)}.{" "}
              <button type="button" onClick={tryAgain}>
                Try again
              </button>
            </p>
          ) : (
            readyAt !== null && (
              <p className="ready-note" data-testid="today-ready">
                Day ready offline · refreshed {clockTime(new Date(readyAt), org.time_zone)}
              </p>
            )
          )}
          <p role="status" data-testid="today-status" className={status && (!status.ok || statusEntry?.state === "failed") ? "error" : undefined}>
            {statusText}
            {status?.session && (
              <>
                {" "}
                <button type="button" onClick={() => void signOut(auth)}>
                  Sign in again
                </button>
              </>
            )}
          </p>
          {mine.length === 0 || !shown ? <NoRoute base={base} canVisit={canVisit} /> : mine.map((route) => routeCard(shown, route))}
        </>
      )}
    </main>
  );
}

/** The empty Today page, with the next step when the user may record a visit. */
function NoRoute({ base, canVisit }: { base: string; canVisit: boolean }) {
  return (
    <>
      <p data-testid="today-none">You have no route today.</p>
      {canVisit && (
        <p data-testid="today-none-visit">
          To record a visit at an account, open{" "}
          <a href={`${base}/visit`} onClick={go}>
            Record a visit
          </a>
          .
        </p>
      )}
    </>
  );
}

/** The save button that the driver taps after a refused stop entry is back in the form. */
const refusedButton = (entry: OutboxEntry) => stopButton(entryOutcome(entry), false);

/** A Not saved entry at its stop or route: the reason, and the way to the Sync page. */
function NotSaved({
  entry,
  base,
  button = "Save",
  pickupSaved = false,
  saved = null,
  change = false,
  onChange,
}: {
  entry: OutboxEntry;
  base: string;
  button?: string;
  pickupSaved?: boolean;
  saved?: ReturnType<typeof serverGallons>;
  change?: boolean;
  onChange?: () => void;
}) {
  const here = onChange !== undefined && canChange(entry.reason);
  const hintId = `fix-hint-${entry.id}`;
  const reason = !entry.reason ? null : change ? changeRefusalText(entry.reason, button) : entry.kind === "stop" ? entryRefusalText(todayReason(entry.reason, saved), button) : withButton(entry.reason, button);
  if (pickupSaved) {
    return (
      <p className="error" role="alert" data-testid="today-not-saved" data-pickup-saved="true">
        {pickupSavedText(entry)}{" "}
        <a href={`${base}/sync`} onClick={go}>
          Open Sync
        </a>
      </p>
    );
  }
  return (
    <>
      <p className="error" role="alert" data-testid="today-not-saved">
        Not saved: {entry.label} {reason}{" "}
        {here && (
          <>
            <button id={`fix-${entry.id}`} type="button" data-testid="today-fix" aria-describedby={hintId} onClick={onChange}>
              {CHANGE_LABEL}
            </button>{" "}
          </>
        )}
        {entry.kind === "visit" && canChange(entry.reason) && (
          <>
            <a href={`${base}/visit?fix=${encodeURIComponent(entry.id)}`} onClick={go} data-testid="today-fix-visit">
              {CHANGE_LABEL}
            </a>{" "}
          </>
        )}
        <a href={`${base}/sync`} onClick={go}>
          Open Sync
        </a>
      </p>
      {here && (
        <p id={hintId} className="sub" data-testid="today-fix-hint">
          {changeHint(button)}
        </p>
      )}
    </>
  );
}
